import TableScrollShell from "../common/TableScrollShell";

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import {
  createAssignmentRule,
  assignmentRules,
  updateFunction,
} from "../../service/Configuration";
import { Search, X, Plus, Pencil, Trash2, Map, Inbox, SearchX } from "lucide-react";
import { fetchFieldAgentMap, fetchFieldAgentMapProject } from "../../service/Task";
import { deleteAssignmentRule } from "../../service/Configuration";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ReactSelector from "../common/CommonReactSelector";
import { components } from 'react-select';
import DeletePermission from "../suspence/DeletePermission";
import ActivityMapFilter from "./AssignmentRule/ActivityMapFilter";
import AssignmentRuleModal from "./AssignmentRuleModal";
import { getPermissionByScreenCode } from "../../utils/permissions";
import { Spinner } from "../../Spinner";
import PageSizeSelector from "../common/PageSizeSelector";
import PagerArrowButton from "../common/PagerArrowButton";
import ActionIconButton from "../common/ActionIconButton";
import ProjectAutoAssignToggle from "./ProjectAutoAssignToggle";
import SearchFieldPicker from "../common/SearchFieldPicker";
import OverflowTooltipText from "../common/OverflowTooltipText";
import {
  AssignScopeTabs,
  currentUserCanUseGroups,
  loadGroupsForDepartment,
  normalizeGroups,
} from "../task/GroupAssignParts";

// Same field-scoped search pattern as AssignmentTable.js (Special Assignment
// Rule) and UserToolbar.js (User Management): a joined field-picker + search
// box via the shared SearchFieldPicker component, instead of one box
// silently matching every field at once. Default scope matches this table's
// first data column (Assign To), same rule AssignmentTable.js follows.
const SEARCH_SCOPES = [
  { value: "assignTo", labelKey: "assignTo", fallback: "Assign To" },
  { value: "rulename", labelKey: "rulename", fallback: "Rule Name" },
  { value: "criteria", labelKey: "criteria", fallback: "Criteria" },
  { value: "status", labelKey: "status", fallback: "Status" },
];

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

// Deterministic per-name color + "Assign To" avatar cell -- same local,
// file-owned helper pattern UserList.js uses for its own table cells
// (nameToHslColor/initials/MultiValueCell etc. all live in that file too,
// not in a shared component module).
const AVATAR_PALETTE = [
  "bg-indigo-500", "bg-violet-500", "bg-rose-500", "bg-amber-500",
  "bg-teal-500", "bg-blue-500", "bg-fuchsia-500", "bg-cyan-600",
];
function avatarColor(name) {
  const value = name || "";
  let sum = 0;
  for (let i = 0; i < value.length; i++) sum += value.charCodeAt(i);
  return AVATAR_PALETTE[sum % AVATAR_PALETTE.length];
}
function initial(name) {
  return (name || "?").trim().charAt(0).toUpperCase();
}
function AssignToCell({ name, scopeLabel }) {
  if (!name) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex items-center gap-3">
      <div className={`h-8 w-8 rounded-full ${avatarColor(name)} text-white text-xs font-semibold flex items-center justify-center shrink-0`}>
        {initial(name)}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate max-w-[160px]">{name}</p>
        <p className="text-xs text-slate-500 truncate">{scopeLabel}</p>
      </div>
    </div>
  );
}

const getRuleAssignScope = (assignment) =>
  assignment?.assignScope ??
  (assignment?.groupId != null && assignment?.groupId !== "" ? "groups" : "users");

const enrichAssignmentScope = (list) =>
  (Array.isArray(list) ? list : []).map((assignment) => ({
    ...assignment,
    assignScope:
      assignment?.groupId != null && assignment?.groupId !== ""
        ? "groups"
        : assignment?.assignScope ?? "users",
  }));

// search/searchScope are lifted to ConfigurationMain.js (which stays mounted
// while switching tabs) so the typed term and selected field survive a tab
// switch instead of resetting -- see AssignmentTable.js for the same change.
const AssignmentRules = ({ tabBar, search, onSearchChange: setSearch, searchScope, onSearchScopeChange: setSearchScope }) => {
  const [selectedUserName, setSelectedUserName] = useState(0);
  const [error, setError] = useState(null);
  const [assignmentList, setAssignmentList] = useState([]);
  const {
    setFieldAgentList,
    setSelectedUserId,
    selectedOrgId,
    projectId,
    theme,
  } = useContext(AppContext);
  const [fieldAgent, setFieldAgent] = useState([])
  const [groups, setGroups] = useState([]);
  const [showCriteria, setshowCriteria] = useState(false);
  const [criteria, setCriteria] = useState("");
  const [totalCount, setTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const { langMode } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";
  const ruleNameRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [createAssignScope, setCreateAssignScope] = useState("users");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const totalPages = Math.ceil(totalCount / pageSize) - 1;
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalCount;
  const displayStart = totalItems > 0 ? startIndex + 1 : 0;
  const displayEnd = totalItems > 0 ? Math.min(endIndex, totalItems) : 0;
  const [deleteRule, setDeleteRule] = useState(null);
  const [openModal, setOpenModal] = useState(false)
  const [userId,setUserId] = useState()
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const customVarPerm = getPermissionByScreenCode("ASSIGNMENT_RULES");
  const canUseGroups = currentUserCanUseGroups();

  const groupOptions = (groups || []).map((g) => ({
    value: g.groupId,
    label: g.groupName,
  }));

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleFieldAgentSelect = (selectedOption) => {
    setSelectedAgents(selectedOption ? selectedOption.value : null);
  };

  const handleCreateScopeChange = (scope) => {
    setCreateAssignScope(scope);
    if (scope === "users") {
      setSelectedGroupId(null);
    } else {
      setSelectedAgents(null);
    }
  };

  const resetCreateForm = () => {
    if (ruleNameRef.current) ruleNameRef.current.value = "";
    setCriteria("");
    setSelectedUserName("");
    setSelectedAgents(null);
    setSelectedGroupId(null);
    setCreateAssignScope("users");
    setIsActive(true);
  };

  const closeCreateModal = () => {
    resetCreateForm();
    setshowCriteria(false);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const ruleName = ruleNameRef.current.value;
    const assignToGroup = canUseGroups && createAssignScope === "groups";
    if (assignToGroup && !selectedGroupId) {
      toast.error(translations[langMode].selectGroup || "Select Group");
      return;
    }
    if (!assignToGroup && !selectedAgents) {
      toast.error(translations[langMode].selectFieldAget);
      return;
    }
    try {
      const responseData = await createAssignmentRule(
        projectId,
        criteria,
        ruleName,
        isActive,
        assignToGroup ? null : selectedAgents,
        assignToGroup ? selectedGroupId : null
      );
      if (responseData.header.code === 600) {
        toast.success(translations[langMode].rulecreated);
        fetchAssignmentRules();
        setDropdownOpen(false);
        closeCreateModal();
      } else {
        toast.error(responseData?.body?.value || `${translations[langMode].errorsubmiting}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(`${translations[langMode].errorsubmiting}: ${error.message}`);
    }
  };

  const fetchAssignmentRules = async () => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      await assignmentRules(
        false,
        projectId,
        currentPage,
        pageSize,
        (list) => setAssignmentList(enrichAssignmentScope(list)),
        setError,
        setTotalCount
      );
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching assignment rules:", error);
      setIsLoading(false);
    }
  };
  // Reset to the first page whenever the project/org context changes.
  useEffect(() => {
    setAssignmentList([]);
    setHasMore(true);
    setCurrentPage(0);
  }, [selectedOrgId, projectId]);

  // Single source of truth for fetching the list.
  useEffect(() => {
    if (!projectId) return;
    fetchAssignmentRules();
  }, [projectId, currentPage, pageSize]);

  const handlAddCriteria = () => {
    if (!projectId) {
      toast.error(translations[langMode].plzselworkflow);
      return;
    }
    setshowCriteria((prevShowCriteria) => !prevShowCriteria);
  };

  const openEditModal = (index) => {
    setEditingIndex(index);
    setEditDraft({ ...assignmentList[index] });
  };

  const closeEditModal = () => {
    setEditingIndex(null);
    setEditDraft(null);
  };

  const handleUpdate = async (assignment) => {
    const isGroupScope = getRuleAssignScope(assignment) === "groups";
    if (isGroupScope && !assignment.groupId) {
      toast.error(translations[langMode].selectGroup || "Select Group");
      return;
    }
    if (!isGroupScope && !assignment.userId) {
      toast.error(translations[langMode].selectFieldAget);
      return;
    }
    const updatedUserIdList = assignment.userId;
    try {
      await updateFunction(
        assignment,
        projectId,
        fetchAssignmentRules,
        toast,
        setDropdownOpen,
        updatedUserIdList,
        langMode,
        closeEditModal
      );
      setOpenDropdownIndex(null);
    } catch (error) {
      console.error("Error handling update:", error);
    }
  };
  useEffect(() => {
    const fetchFieldAgents = async () => {
      try {
        const agents = await fetchFieldAgentMap(selectedOrgId);
        setFieldAgentList(agents.body.value);
      } catch (error) {
        console.error("An error occurred:", error);
      }
    };
    fetchFieldAgents();
  }, [selectedOrgId]);

  useEffect(() => { }, [fieldAgent]);
  const handleAssigneeChange = (selectedId) => {
    setSelectedUserId(selectedId);
    const selectedAgent = fieldAgent.find(
      (agent) => agent.userId === selectedId
    );
    if (selectedAgent) {
      setSelectedUserName(selectedAgent.name);
    }
  };
  const handleToggle = () => {
    setIsActive((prevIsActive) => !prevIsActive);
  };

  const setDraftAssignScope = (scope) => {
    setEditDraft((d) =>
      scope === "groups"
        ? { ...d, assignScope: scope, userId: null }
        : { ...d, assignScope: scope, groupId: null, groupName: null }
    );
  };

  const handleDeleteRule = async () => {
    setIsLoading(true);
    try {
      const response = await deleteAssignmentRule(deleteRule);
      if (response.header.code === 600) {
        toast.success(translations[langMode].ruledeleted);
        setAssignmentList((prevRules) =>
          prevRules.filter((rule) => rule.id !== deleteRule)
        );
        setDeleteRule(null)
        fetchAssignmentRules()
      } else {
        toast.error(response?.body?.value || translations[langMode].failedelrule);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error deleting completion rule:", error);
      toast.error(translations[langMode].failedelrule);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleFieldAgent = async () => {
      try {
        const result = await fetchFieldAgentMapProject(projectId)
        setFieldAgent(result.body.value)
      } catch (error) {
        console.error("Failed to fetch field agents:", error);
      }
    }

    if (projectId) {
      handleFieldAgent()
    }

  }, [projectId])

  useEffect(() => {
    const loadGroups = async () => {
      if (!canUseGroups) {
        setGroups([]);
        return;
      }
      const raw = await loadGroupsForDepartment();
      setGroups(normalizeGroups(raw));
    };
    loadGroups();
  }, [canUseGroups, selectedOrgId]);

  const renderAssignPicker = ({
    scope,
    onScopeChange,
    userValue,
    onUserChange,
    groupValue,
    groupLabel,
    onGroupChange,
    selectorHeight = "44px",
  }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="block text-sm font-semibold text-gray-900 dark:text-white">
        {translations[langMode].assignTo}
      </label>
      {canUseGroups && (
        <AssignScopeTabs
          scope={scope}
          onScopeChange={onScopeChange}
          langMode={langMode}
          userLabel={translations[langMode]?.userAssign?.replace(/\s*Assign$/i, "") || "User"}
          groupLabel={translations[langMode]?.groupAssign?.replace(/\s*Assign$/i, "") || "Group"}
        />
      )}
      <div className="w-full">
        {scope === "groups" && canUseGroups ? (
          <ReactSelector
            value={
              groupValue
                ? groupOptions.find((option) => option.value === groupValue)
                  || { value: groupValue, label: groupLabel || String(groupValue) }
                : null
            }
            options={groupOptions}
            isSearchable={true}
            height={selectorHeight}
            menuPlacement="bottom"
            menuPortalTarget={document.body}
            components={{ Placeholder }}
            placeholder={translations[langMode].selectGroup}
            onChange={onGroupChange}
            isClearable={true}
            isMulti={false}
            overflow="overlay"
          />
        ) : (
          <ReactSelector
            value={userValue}
            options={(fieldAgent || [])?.map((item) => ({ value: item?.userId, label: item?.userName }))}
            isSearchable={true}
            height={selectorHeight}
            menuPlacement="bottom"
            menuPortalTarget={document.body}
            components={{ Placeholder }}
            placeholder={translations[langMode].selectFieldAget}
            onChange={onUserChange}
            isClearable={true}
            isMulti={false}
            overflow="overlay"
          />
        )}
      </div>
    </div>
  );

  // Small "User"/"Group" badge label for the table's Assign To column --
  // reuses the same " Assign" suffix trim already used for the AssignScopeTabs
  // labels above, no new translation keys needed.
  const scopeBadgeLabel = (scope) =>
    scope === "groups"
      ? (translations[langMode]?.groupAssign?.replace(/\s*Assign$/i, "") || "Group")
      : (translations[langMode]?.userAssign?.replace(/\s*Assign$/i, "") || "User");

  const getTargetName = (assignment) => {
    const scope = getRuleAssignScope(assignment);
    if (scope === "groups") return assignment.groupName || null;
    return fieldAgent.find((a) => a.userId === assignment.userId)?.userName || null;
  };

  // Client-side filter over the currently loaded page -- same pattern as
  // CompletionRules.js's `displayedRules` (no new API params, pagination
  // untouched). Keeps each row's original index (needed by the per-row
  // handlers above, all of which mutate assignmentList by index).
  //
  // Field-scoped search -- same architecture as AssignmentTable.js/
  // UserList.js: each scope resolves to only the field it should match
  // against. Status uses startsWith, not includes -- "Inactive" contains
  // "active" as a literal substring (in-ACTIVE-), so an includes() check
  // would make typing "active" also match Inactive rows.
  const displayedAssignments = useMemo(() => {
    const indexed = assignmentList.map((assignment, index) => ({ assignment, index }));
    if (!search.trim()) return indexed;
    const q = search.trim().toLowerCase();
    const activeLabel = (translations[langMode].active || "Active").toLowerCase();
    const inactiveLabel = (translations[langMode].inactive || "Inactive").toLowerCase();

    const matchesAssignTo = (assignment) => (getTargetName(assignment) || "").toLowerCase().includes(q);
    const matchesRuleName = (assignment) => (assignment.ruleName || "").toLowerCase().includes(q);
    const matchesCriteria = (assignment) => (assignment.rule || "").toLowerCase().includes(q);
    const matchesStatus = (assignment) => (assignment.activeStatus ? activeLabel : inactiveLabel).startsWith(q);

    return indexed.filter(({ assignment }) => {
      if (searchScope === "rulename") return matchesRuleName(assignment);
      if (searchScope === "criteria") return matchesCriteria(assignment);
      if (searchScope === "status") return matchesStatus(assignment);
      return matchesAssignTo(assignment);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentList, search, searchScope, fieldAgent, langMode]);

  const scopeOptions = SEARCH_SCOPES.map((s) => ({
    value: s.value,
    label: translations[langMode][s.labelKey] || s.fallback,
  }));
  const currentScopeLabel = scopeOptions.find((s) => s.value === searchScope)?.label;
  // Changing the search field makes the previously typed term ambiguous (it
  // was typed against a different field) -- same reset UserToolbar.js does
  // in its own handleScopeSelect.
  const handleScopeSelect = (value) => {
    setSearchScope(value);
    setSearch("");
  };
  const currentSearchPlaceholder =
    searchScope === "assignTo"
      ? translations[langMode].searchByAssignTo
      : `${translations[langMode]?.search || "Search"} ${currentScopeLabel || ""}…`;

  const editingAssignment = editingIndex !== null ? editDraft : null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="shrink-0 flex flex-col gap-3 mb-3 rounded-xl border border-slate-200 bg-white shadow-sm">
        {tabBar && (
          <div className="flex w-full min-w-0 items-center justify-between gap-2 px-3 pt-3 sm:px-5 sm:pt-4">
            {tabBar}
          </div>
        )}
        <div className="flex flex-col gap-3 px-3 py-3 sm:px-5 sm:py-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-800 leading-tight sm:text-lg">
              {translations[langMode].assignment}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {translations[langMode].assignmentRuleSubheading}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Joined button+input pill -- same visual/interaction pattern as
                AssignmentTable.js (Special Assignment Rule) and UserToolbar.js
                (User Management) via the shared SearchFieldPicker component,
                so this table's search looks and behaves like the rest of the
                app's field-scoped list searches. */}
            <div className="flex items-center w-full sm:w-72">
              <SearchFieldPicker
                options={scopeOptions}
                activeValue={searchScope}
                onSelect={handleScopeSelect}
                searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
                isRtl={isRtl}
                buttonTestId="assignment-rules-search-scope-button"
                buttonClassName="!h-10"
              />
              <div className="relative flex-1 flex items-center">
                <Search size={14} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 shrink-0 ${isRtl ? "right-3" : "left-3"}`} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={typeof window !== "undefined" && window.innerWidth < 450 ? `${translations[langMode]?.search || "Search"}...` : currentSearchPlaceholder}
                  title={currentSearchPlaceholder}
                  aria-label={currentSearchPlaceholder}
                  className={`w-full h-10 text-sm outline-none bg-white border border-slate-200 focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] placeholder:text-slate-400 ${isRtl ? "pr-9 pl-8 border-r-0 rounded-l-lg" : "pl-9 pr-8 border-l-0 rounded-r-lg"}`}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className={`absolute top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 shrink-0 ${isRtl ? "left-2" : "right-2"}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="grid w-full grid-cols-2 gap-2 sm:contents">
              <ProjectAutoAssignToggle
                disabled={!customVarPerm?.canUpdate}
                wrapperClassName="w-full sm:w-auto"
                className="w-full sm:w-auto justify-center"
              />
              <CommonButton colorIntent="primary"
                onClick={handlAddCriteria}
                variant="primary"
                tooltipText={translations[langMode].addassignmentrule}
                tooltipPosition="top"
                disabled={!customVarPerm?.canCreate}
                wrapperClassName="w-full sm:w-auto"
                className="w-full sm:w-auto !h-10 !min-h-[40px] !rounded-[10px] !px-3 !py-2 !text-xs sm:!px-4 sm:!text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                <Plus size={18} /> {translations[langMode].NewCriteria}
              </CommonButton>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="relative z-20 flex flex-wrap items-center justify-between gap-2 sm:gap-3 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
          <p className="flex items-center gap-2 text-xs text-slate-500 sm:gap-4">
            <span className="text-xs text-slate-500 whitespace-nowrap sm:hidden">
              <span className="rtl-keep-ltr">{`${displayStart}-${displayEnd}`}</span>
              {` / `}
              <span className="rtl-keep-ltr">{totalItems ?? 0}</span>
            </span>
            <span className="hidden sm:inline whitespace-nowrap">
              {`${translations[langMode].showing || "Showing"} `}
              <span className="rtl-keep-ltr">{`${displayStart}-${displayEnd}`}</span>
              {` ${translations[langMode].of || "of"} `}
              <span className="rtl-keep-ltr">{totalItems ?? 0}</span>
            </span>
          </p>
          <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
            <div className="flex items-center gap-1.5">
              <label htmlFor="pageSize" className="hidden sm:inline whitespace-nowrap">
                {translations[langMode].rows}
              </label>
              <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
            </div>
            <span className="pager-nav flex items-center gap-1">
              <PagerArrowButton
                direction="prev"
                isRtl={isRtl}
                onClick={goToPreviousPage}
                disabled={currentPage === 0}
                ariaLabel={translations[langMode]?.previousPage || "Previous page"}
              />
              <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                {currentPage + 1} / {Math.max(totalPages + 1, 1)}
              </span>
              <PagerArrowButton
                direction="next"
                isRtl={isRtl}
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                ariaLabel={translations[langMode]?.nextPage || "Next page"}
              />
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="py-10">
            <Spinner />
          </div>
        ) : (
          <TableScrollShell theme={theme} className="w-full flex-1 min-h-0" innerClassName="task-list-scrollbar">
            <table className="w-full text-xs border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-center border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-400 w-14">
                    {translations[langMode].number}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].assignTo}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].rulename}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].criteria}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].activedeactive}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-right border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].Action}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedAssignments.map(({ assignment, index }) => {
                  const scope = getRuleAssignScope(assignment);
                  const targetName = getTargetName(assignment);
                  return (
                    <tr
                      key={assignment.id ?? index}
                      className="hover:bg-slate-50/70 transition-colors"
                    >
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 text-center">
                        <span className="font-mono text-xs text-[#70788f] whitespace-nowrap">
                          {currentPage * pageSize + index + 1}
                        </span>
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                        <AssignToCell name={targetName} scopeLabel={scopeBadgeLabel(scope)} />
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                        {assignment.ruleName ? (
                          <OverflowTooltipText text={assignment.ruleName} className="font-mono text-xs text-slate-500 max-w-[180px]" />
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 max-w-60">
                        {assignment.rule ? (
                          <OverflowTooltipText text={assignment.rule} className="font-mono text-xs text-slate-500" />
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 whitespace-nowrap">
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md"
                          style={
                            assignment.activeStatus
                              ? { color: "#188A4B", background: "#E7F6ED" }
                              : { color: "#C22B3B", background: "#FBE9EB" }
                          }
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: assignment.activeStatus ? "#188A4B" : "#C22B3B" }}
                          />
                          {assignment.activeStatus
                            ? translations[langMode].active || "Active"
                            : translations[langMode].inactive || "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                        <div className="flex items-center justify-end gap-1.5">
                          <ActionIconButton
                            tone="blue"
                            ariaLabel={translations[langMode].editRule}
                            tooltipText={translations[langMode].editRule}
                            tooltipPosition="top"
                            onClick={() => openEditModal(index)}
                            className="!h-7 !w-7 !rounded-md"
                          >
                            <Pencil size={14} />
                          </ActionIconButton>
                          <ActionIconButton
                            tone="red"
                            disabled={!customVarPerm?.canDelete}
                            ariaLabel={translations[langMode].deleteRule}
                            tooltipText={translations[langMode].deleteRule}
                            tooltipPosition="top"
                            onClick={() => setDeleteRule(assignment.id)}
                            className="!h-7 !w-7 !rounded-md"
                          >
                            <Trash2 size={14} />
                          </ActionIconButton>
                          <ActionIconButton
                            tone="emerald"
                            disabled={assignment.userId == null}
                            ariaLabel={translations[langMode].selectActivityAndMap}
                            tooltipText={translations[langMode].selectActivityAndMap}
                            tooltipPosition="top"
                            onClick={() => {
                              setOpenModal(true);
                              setUserId(assignment.userId);
                            }}
                            className="!h-7 !w-7 !rounded-md"
                          >
                            <Map size={14} />
                          </ActionIconButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {displayedAssignments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-16 h-[200px] align-middle">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-[#E0EBF4] text-[#236DB4]">
                          {search.trim() ? <SearchX size={22} /> : <Inbox size={22} />}
                        </div>
                        <div className="text-sm font-semibold text-slate-800">
                          {search.trim() ? translations[langMode].noSearchResults : translations[langMode].norule}
                        </div>
                        <div className="text-xs mt-1 max-w-xs text-slate-400">
                          {search.trim() ? translations[langMode].tryAdjustingSearch : translations[langMode].getStartedAddingRule}
                        </div>
                        {search.trim() ? (
                          <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="mt-3 h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                          >
                            {translations[langMode].clearSearch}
                          </button>
                        ) : (
                          customVarPerm?.canCreate && (
                            <button data-button-intent="primary"
                              type="button"
                              onClick={handlAddCriteria}
                              className="mt-3 h-8 px-3 rounded-lg text-xs font-medium text-white bg-[#236DB4] hover:bg-[#1d5a96]"
                            >
                              {translations[langMode].NewCriteria}
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScrollShell>
        )}
      </div>

      <AssignmentRuleModal
        open={showCriteria}
        onClose={closeCreateModal}
        isNew
        ruleNameInputProps={{ id: "areaCode", ref: ruleNameRef }}
        assignToNode={renderAssignPicker({
          scope: createAssignScope,
          onScopeChange: handleCreateScopeChange,
          userValue: selectedAgents
            ? fieldAgent
              .map((item) => ({ value: item?.userId, label: item?.userName }))
              .find((option) => option.value === selectedAgents)
            : null,
          onUserChange: handleFieldAgentSelect,
          groupValue: selectedGroupId,
          onGroupChange: (selectedOption) =>
            setSelectedGroupId(selectedOption ? selectedOption.value : null),
        })}
        isActive={isActive}
        onToggleActive={handleToggle}
        criteria={criteria}
        onCriteriaChange={(e) => setCriteria(e.target.value)}
        onSave={() => handleSubmit()}
        saveDisabled={!customVarPerm?.canCreate}
      />

      {editingAssignment && (
        <AssignmentRuleModal
          key={editingAssignment.id ?? editingIndex}
          open={!!editingAssignment}
          onClose={closeEditModal}
          isNew={false}
          ruleNumber={currentPage * pageSize + editingIndex + 1}
          ruleNameInputProps={{
            id: `ruleName-${editingIndex}`,
            value: editingAssignment.ruleName,
            onChange: (e) => setEditDraft((d) => ({ ...d, ruleName: e.target.value })),
          }}
          assignToNode={renderAssignPicker({
            scope: getRuleAssignScope(editingAssignment),
            onScopeChange: setDraftAssignScope,
            userValue: editingAssignment?.userId
              ? fieldAgent
                .map((item) => ({ value: item?.userId, label: item?.userName }))
                .find((option) => option.value === editingAssignment.userId)
              : null,
            onUserChange: (selectedValue) =>
              setEditDraft((d) => ({
                ...d,
                userId: selectedValue ? selectedValue.value : null,
                groupId: null,
                groupName: null,
                assignScope: "users",
              })),
            groupValue: editingAssignment?.groupId || null,
            groupLabel: editingAssignment?.groupName,
            onGroupChange: (selectedValue) =>
              setEditDraft((d) => ({
                ...d,
                groupId: selectedValue ? selectedValue.value : null,
                groupName: selectedValue ? selectedValue.label : null,
                userId: null,
                assignScope: "groups",
              })),
          })}
          isActive={editingAssignment.activeStatus}
          onToggleActive={() => setEditDraft((d) => ({ ...d, activeStatus: !d.activeStatus }))}
          criteria={editingAssignment.rule}
          onCriteriaChange={(e) => setEditDraft((d) => ({ ...d, rule: e.target.value }))}
          onSave={() => handleUpdate(editingAssignment)}
          saveDisabled={!customVarPerm?.canUpdate}
        />
      )}

      {deleteRule !== null && <DeletePermission
        deleteFunction={handleDeleteRule}
        onClose={() => setDeleteRule(null)}
        onDeleteText={translations[langMode].aresurewantodeleterule}
      />}

      <ActivityMapFilter openModal={openModal} setOpenModal={setOpenModal} userId={userId} />
    </div>
  );
};
export default AssignmentRules;
